import { PartyPopperIcon } from 'lucide-react'

import { Link } from '@/components'
import { Button } from '@/components/ui'
import {
  type DuplicateGroup,
  duplicateReviewPath,
  duplicatesPath,
} from '@/modules/insights/utils'

interface ResolvedPanelProps {
  slug: string
  worklist: DuplicateGroup[]
}

// Shown once a group is gone: its rows were deleted, it was marked as not duplicates, or the link
// names a group this project no longer has.
export const ResolvedPanel = ({ slug, worklist }: ResolvedPanelProps) => {
  const next = worklist[0]

  return (
    // Stays full screen like the workspace it replaces, so finishing a group does not dump the user
    // back onto the page underneath before they have picked what to do next.
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-4 bg-background p-10 text-center">
      <PartyPopperIcon className="size-6 text-emerald-500" />
      <div className="space-y-1">
        <p className="text-sm font-semibold text-foreground">
          Nothing left to review in this group
        </p>
        <p className="text-xs text-muted-foreground">
          {worklist.length > 0
            ? `${worklist.length} ${worklist.length === 1 ? 'group' : 'groups'} still need a look.`
            : 'Every duplicate group in this project has been reviewed.'}
        </p>
      </div>
      <div className="flex flex-wrap items-center justify-center gap-2">
        {next && (
          <Button asChild size="sm">
            <Link to={duplicateReviewPath(slug, next.id)} preload="intent">
              Next group
            </Link>
          </Button>
        )}
        <Button theme="outline" size="sm" asChild>
          <Link to={duplicatesPath(slug)} preload="intent">
            Back to duplicates
          </Link>
        </Button>
      </div>
    </div>
  )
}
